import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import ClIcon from '../ClIcon';
import ClMenu from '../ClMenu';
import { get } from '../../lib';

function areEqualValues(a: any, b: any) {
  if (typeof b === 'object' && b !== null) {
    return a === b;
  }

  return String(a) === String(b);
}

@Component({
  components: { ClIcon, ClMenu }
})
export default class ClSelect extends Vue {
  /**
   * If true, the width of the popover will automatically be set according to the items inside the
   * menu, otherwise it will be at least the width of the select input.
   */

  @Prop({
    type: Boolean,
    default: false
  })
  public autoWidth!: boolean;

  /**
   * Override or extend the styles applied to the component.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public classes!: Object;

  @Prop({
    type: Boolean,
    default: false
  })
  public disabled!: boolean;

  /**
   * If `true`, the selected item is displayed even if its value is empty.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public displayEmpty!: boolean;

  /**
   * Attributes applied to the `input` element.
   * When `native` is `true`, the attributes are applied on the `select` element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public inputProps!: Object;

  /**
   * Properties applied to the Menu element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public ClMenuProps!: Object;

  private menuAnchor: Element | null = null;

  /**
   * If true, `value` must be an array and the menu will support multiple selections.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public multiple!: boolean;

  /**
   *
   */
  @Prop({
    type: String,
    default: ''
  })
  public name!: string;

  /**
   * If `true`, the component will use a native `select` element.
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public native!: boolean;

  /**
   * Control `select` open state.
   * You can only use it when the `native` property is `false` (default).
   */
  @Prop({
    type: Boolean,
    default: false
  })
  public open!: boolean;

  public openState: boolean = this.open;

  @Prop({
    type: Function
  })
  public renderValue!: (value: any) => string | Vue;

  /**
   * Properties applied to the clickable div element.
   */
  @Prop({
    type: Object,
    default: () => ({})
  })
  public selectDisplayProps!: object;

  private type: string = 'hidden';

  /**
   * The input value.
   * This property is required when the `native` property is `false` (default).
   */
  @Prop({
    type: [String, Number, Boolean, Object, Array]
  })
  public value!: any;

  private internalValue: any = this.value;

  get computedValue(): any {
    const value = this.internalValue || this.value;
    return Array.isArray(value) ? value.join(',') : value;
  }

  get display(): string | Vue {
    if (!this.computedValue && !this.displayEmpty) {
      return '';
    }

    if (this.renderValue) {
      return this.renderValue(this.computedValue);
    }

    const slot = this.$scopedSlots.default
      ? this.$scopedSlots.default({ optionListeners: this.optionListeners }) ||
        []
      : [];
    const menuItems = slot.filter(
      node => get(node, 'componentOptions.tag') === 'cl-menu-item'
    );
    let text = '';
    menuItems.forEach(item => {
      if (
        get(item, 'componentOptions.propsData.value') === this.computedValue
      ) {
        text = get(item, 'componentOptions.children[0].text', '').trim();
        return;
      }
    });
    return text;
  }

  get menuId(): string {
    return `menu-${this.name || ''}`;
  }

  get menuMinWidth(): number | null {
    const { selectDisplay } = this.$refs;
    if (this.autoWidth || !selectDisplay) {
      return null;
    }
    return (selectDisplay as Element).clientWidth;
  }

  get optionListeners(): object {
    return {
      click: this.handleOptionClick
    };
  }

  handleClick(event: Event) {
    if (!this.disabled) {
      this.menuAnchor = event.currentTarget as Element;
    }
  }

  handleOptionClick(event: Event, component: Vue) {
    this.menuAnchor = null;
    const { value } = component as any;
    this.internalValue = value;
    this.$emit('input', value);
    this.$emit('change', value);
  }

  handleMenuClose() {
    this.menuAnchor = null;
  }

  @Watch('value')
  handleValueChange(nextValue: any) {
    this.internalValue = nextValue;
  }
}
